// @spec NIS2-STR-038
'use client';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Shield } from 'lucide-react';
import { Toaster } from 'sonner';

import { useTranslation } from 'react-i18next';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { AuditorModeBanner } from '@/features/auditor-access/components/AuditorModeBanner';
import { AuditorNIS2Overview } from '@/features/auditor-access/components/AuditorNIS2Overview';
import { useAuditorStore } from '@/stores/auditor-store';

export const Route = createFileRoute('/auditor/access/$accessId/nis2')({
  component: AuditorNIS2Page,
});

/**
 * Auditor NIS2 compliance overview page.
 *
 * Displays a read-only view of NIS2 compliance data for an organization
 * that the auditor has access to review.
 */
function AuditorNIS2Page() {
  const { accessId } = Route.useParams();
  const navigate = useNavigate();
  const { t } = useTranslation('auditor-access');

  const isAuditorMode = useAuditorStore.use.isAuditorMode();
  const currentAccess = useAuditorStore.use.currentAccess();

  // Verify user is in auditor mode and has the correct access ID
  if (!isAuditorMode || !currentAccess) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-4xl">
          <Alert variant="destructive">
            <Shield className="h-4 w-4" />
            <AlertTitle>{t('routes.accessRequired')}</AlertTitle>
            <AlertDescription>
              {t('routes.accessRequiredDescription')}
            </AlertDescription>
          </Alert>
          <Button variant="outline" className="mt-4" onClick={() => void navigate({ to: '/' })}>
            {t('routes.goToHome')}
          </Button>
        </div>
      </div>
    );
  }

  // Verify the access ID matches the current auditor mode
  if (currentAccess.accessId !== accessId) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-4xl">
          <Alert variant="destructive">
            <Shield className="h-4 w-4" />
            <AlertTitle>{t('routes.invalidAccess')}</AlertTitle>
            <AlertDescription>
              {t('routes.invalidAccessDescription')}
            </AlertDescription>
          </Alert>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => void navigate({ to: `/auditor/access/${currentAccess.accessId}/nis2` })}
          >
            {t('routes.goToYourNIS2Overview')}
          </Button>
        </div>
      </div>
    );
  }

  // Verify the access scope includes 'nis2_compliance'
  if (!currentAccess.accessScope.includes('nis2_compliance')) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-4xl">
          <Alert variant="destructive">
            <Shield className="h-4 w-4" />
            <AlertTitle>{t('routes.accessDenied')}</AlertTitle>
            <AlertDescription>
              {t('routes.accessDeniedNIS2')}
            </AlertDescription>
          </Alert>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => void navigate({ to: '/dashboard' })}
          >
            {t('routes.goToDashboard')}
          </Button>
        </div>
      </div>
    );
  }

  const handleNavigateToMeasures = () => {
    void navigate({ to: `/auditor/access/${accessId}/nis2/measures` });
  };

  const handleNavigateToAssessments = () => {
    void navigate({ to: `/auditor/access/${accessId}/nis2/assessments` });
  };

  const handleNavigateToEvidence = () => {
    void navigate({ to: `/auditor/access/${accessId}/nis2/evidence` });
  };

  const handleNavigateToGaps = () => {
    void navigate({ to: `/auditor/access/${accessId}/nis2/gaps` });
  };

  const handleNavigateToIncidents = () => {
    void navigate({ to: `/auditor/access/${accessId}/nis2/incidents` });
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Auditor mode banner */}
      <AuditorModeBanner hasOtherOrganizations={false} />

      <div className="p-8">
        <div className="mx-auto max-w-6xl">
          <AuditorNIS2Overview
            accessId={accessId}
            organizationName={currentAccess.name}
            onNavigateToMeasures={handleNavigateToMeasures}
            onNavigateToAssessments={handleNavigateToAssessments}
            onNavigateToEvidence={handleNavigateToEvidence}
            onNavigateToGaps={handleNavigateToGaps}
            onNavigateToIncidents={handleNavigateToIncidents}
          />
        </div>
      </div>

      {/* Toast notifications */}
      <Toaster position="top-right" richColors />
    </div>
  );
}
